import { register } from '../../../JavaScript/Utils'

export type CookieFirstConsentDetail = {
  advertising: boolean
  functional: boolean
  necessary: boolean
  performance: boolean
}

declare global {
  interface Window {
    // source for these types: https://support.cookiefirst.com/hc/de/articles/360011568738-Javascript-API
    // + own observations on the detail object
    // TODO this is still incomplete, but covers our use cases
    CookieFirst?: {
      consent: CookieFirstConsentDetail,
      visitorId: string,
      hasConsented: boolean,
      acceptedServices: { [key: string]: true },
      openPanel(): void,
      acceptCategory(category: string): void,
      acceptAllCategories(): void,
      acceptPreselectedCategories(): void,
      declineAllCategories(): void,
      declineCategory(category: string): void,
      acceptService(...services: string[]): void
      declineService(...services: string[]): void
    }
  }
}

export const CookieConsentContentLoader = (el: HTMLElement) => {
  /**
   * Replaces a script tag that is assigned to a service in CookieFirst.
   * This is modeled after the same functionality that exists in CookieFirst's scripts, but only works for
   * category-based consent there.
   */
  const enableScriptsForService = (serviceName: string): void => {
    const scriptElements: NodeListOf<HTMLScriptElement> = document.querySelectorAll('script[data-cookiefirst-service="' + serviceName + '"]');

    Array.from(scriptElements)
      .filter((e) => e.type === 'text/plain')
      .forEach((element) => {
        const newNode = document.createElement("script");
        Array.from(element.attributes).forEach((e => {
            if (e.name === "data-src") {
              newNode.src = e.value;
            } else {
              newNode.setAttribute(e.name, e.value);
            }
          }
        ));

        newNode.text = element.innerHTML;
        newNode.type = 'application/javascript';

        try {
          element.parentNode.replaceChild(newNode, element);
        } catch (e) {
          console.error('Error while changing type of JS node: ' + e);
        }
      });
  }

  const init = () => {
    const consentElementId = el.dataset.consentElementId
    const noConsentElementId = el.dataset.noConsentElementId
    const cookieCategory = el.dataset.cookieCategory
    const cookieService = el.dataset.cookieService

    /**
     * Handler for events raised by CookieFirst.
     *
     * Be aware that this is (at least sometimes) also called before a user has initially given any consent, that is
     * before the window.CookieFirst object was initialized.
     */
    const consentHandler = function (event: CustomEvent & { detail: CookieFirstConsentDetail }) {

      const consentByCategory = cookieCategory !== '' && event.detail && (event.detail[cookieCategory] ?? null) === true;
      const consentByService = cookieService !== '' && (window.CookieFirst?.acceptedServices[cookieService] ?? null) === true;
      const consent: boolean = consentByCategory || consentByService;
      document.getElementById(consentElementId).style.display = consent ? 'block' : 'none'
      document.getElementById(noConsentElementId).style.display = !consent ? 'block' : 'none'

      if (!consentByCategory && consentByService) {
        enableScriptsForService(cookieService);
      }

      // add onClick event to all elements with data-action='openCookieSettings'
      const container: HTMLElement = document.querySelector('#' + noConsentElementId)
      const buttonElements: NodeListOf<HTMLElement> = container.querySelectorAll('[data-action="openCookieSettings"]')

      buttonElements.forEach(button => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          const CookieFirst = window.CookieFirst;
          CookieFirst.openPanel();
        });
      })
    }
    window.addEventListener('cf_consent', consentHandler) // fired after initial consent
    window.addEventListener('cf_consent_loaded', consentHandler)
  }

  init()
}

register('CookieConsentContentLoader', CookieConsentContentLoader)
